import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { MapPin, CalendarDays, Settings2 } from "lucide-react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { SECTIONS, STATE_META, ROLE_LABEL, Badge, fmtDay } from "@/pages/portal/event-space/shared";
import Overview from "@/pages/portal/event-space/Overview";
import Program from "@/pages/portal/event-space/Program";
import ProfileSection from "@/pages/portal/event-space/ProfileSection";
import Participants from "@/pages/portal/event-space/Participants";
import Networking from "@/pages/portal/event-space/Networking";
import Meetings from "@/pages/portal/event-space/Meetings";
import Documents from "@/pages/portal/event-space/Documents";

const VIEWS = { overview: Overview, program: Program, profile: ProfileSection, participants: Participants, networking: Networking, meetings: Meetings, documents: Documents };

export default function EventSpace() {
  const { slug, section } = useParams();
  const [space, setSpace] = useState(null);
  const [error, setError] = useState(null);
  const reload = useCallback(() => api.get(`/event-space/${slug}`).then((r) => setSpace(r.data)).catch((e) => setError(e.response?.status || 500)), [slug]);
  useEffect(() => { setSpace(null); setError(null); reload(); }, [reload]);

  if (!section) return <Navigate to={`/portal/event/${slug}/overview`} replace />;
  const View = VIEWS[section] || Overview;
  const sec = SECTIONS.find((s) => s.id === section) || SECTIONS[0];
  const ev = space?.event;

  return (
    <PortalLayout>
      {error && <p className="rounded-xl bg-rose-50 p-4 text-sm text-rose-700" data-testid="event-space-error">Événement introuvable.</p>}
      {!space && !error && <div className="text-sm text-neutral-500">Chargement…</div>}
      {space && (
        <div className="space-y-5" data-testid="event-space">
          <header className="relative overflow-hidden rounded-3xl bg-navy p-6 text-white">
            {ev.image && <img src={ev.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
            <div className="relative flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge meta={STATE_META[space.state]} testId="event-space-state" />
                  <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-bold" data-testid="event-space-role">{ROLE_LABEL[space.role]}</span>
                </div>
                <h1 className="mt-2 font-serif text-3xl font-bold sm:text-4xl" data-testid="event-space-title">{ev.title}</h1>
                <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-white/80">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" />{fmtDay(ev.date)}</span>
                  {ev.city && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{ev.city}</span>}
                </p>
              </div>
              <div className="flex gap-2">
                {space.role === "organizer" && <Link to={`/admin/events/${ev.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold text-navy hover:bg-wine-50" data-testid="event-space-admin-link"><Settings2 className="h-4 w-4" />Gérer l'événement</Link>}
                <Link to={`/events/${ev.slug}`} className="rounded-full border border-white/40 px-4 py-2 text-xs font-semibold hover:bg-white/10">Page publique</Link>
              </div>
            </div>
          </header>
          <nav className="flex gap-1 overflow-x-auto rounded-2xl bg-neutral-100 p-1 lg:hidden">
            {SECTIONS.map((s) => <Link key={s.id} to={`/portal/event/${slug}/${s.id}`} className={`whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-semibold ${s.id === section ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`}>{s.label}</Link>)}
          </nav>
          <div>
            <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy" data-testid="event-space-section-title"><sec.icon className="h-5 w-5 text-wine-500" />{sec.label}</h2>
            <p className="text-sm text-neutral-500">{sec.hint}</p>
          </div>
          <View space={space} slug={slug} reload={reload} />
        </div>
      )}
    </PortalLayout>
  );
}
